import type { Customer, CustomerListResponse } from '../models/customerModel';
import {
  normalizeCustomerContactFromApi,
  normalizeMasterAccountFromApi,
} from './customerRequestIntakeNormalize';
import {
  JOURNEY_STAGE_VALUES,
  type CustomerDashboardFields,
  type JourneyStage,
  type SuluStatus,
} from '../models/onboardingDashboard.types';

/** A customer as normalized from the API: list APIs may also send dashboard fields. */
export type NormalizedCustomer = Customer & Partial<CustomerDashboardFields>;

type NormalizedCustomerList = { customers: NormalizedCustomer[]; totalItemCount: number };

const isJourneyStage = (value: string): value is JourneyStage =>
  (JOURNEY_STAGE_VALUES as readonly string[]).includes(value);

const SULU_STATUS_MAP: Record<string, SuluStatus> = {
  notstarted: 'notStarted',
  needspartnermaster: 'needsPartnerMaster',
  inprogress: 'inProgress',
  complete: 'complete',
  failed: 'failed',
};

/** BE may send PascalCase or spaced labels instead of contract camelCase enums. */
export const normalizeSuluStatus = (value: string | undefined | null): SuluStatus | undefined => {
  if (!value) {
    return undefined;
  }
  if (
    value === 'notStarted' ||
    value === 'needsPartnerMaster' ||
    value === 'inProgress' ||
    value === 'complete' ||
    value === 'failed'
  ) {
    return value;
  }
  return SULU_STATUS_MAP[value.replace(/\s+/g, '').toLowerCase()];
};

const STAGE_LABEL_TO_JOURNEY: Record<string, JourneyStage> = {
  request: 'request',
  approval: 'domainApproval',
  domain: 'domainCreate',
  'masterdata': 'masterData',
  complete: 'complete',
};

export const normalizeOnboardingStatusFromApi = (
  status: string | undefined | null
): Customer['onboardingStatus'] => {
  if (!status) {
    return 'Pending';
  }
  if (isJourneyStage(status)) {
    return status;
  }
  const mapped = STAGE_LABEL_TO_JOURNEY[status.replace(/\s+/g, '').toLowerCase()];
  if (mapped) {
    return mapped;
  }
  return status as Customer['onboardingStatus'];
};

type ApiCustomerRecord = Record<string, unknown>;

/**
 * Accepts the API's string ticket or an already shaped `{ key, url? }`.
 * Returns undefined when the field is absent, and null when it is present but empty.
 */
export const normalizeJiraFromApi = (value: unknown): { key: string; url?: string } | null | undefined => {
  if (value === undefined) {
    return undefined;
  }
  if (typeof value === 'string') {
    const key = value.trim();
    return key ? { key } : null;
  }
  if (value && typeof value === 'object' && typeof (value as { key?: unknown }).key === 'string') {
    const { key, url } = value as { key: string; url?: unknown };
    return key.trim()
      ? { key: key.trim(), ...(typeof url === 'string' ? { url } : {}) }
      : null;
  }
  return null;
};

export const normalizeCustomerApiRecord = (
  raw: ApiCustomerRecord | Customer
): NormalizedCustomer => {
  const record = raw as ApiCustomerRecord;
  const onboardingStatusLabel =
    (record.onboardingStatusLabel as string | undefined) ??
    (record.onboardingStatuslabel as string | undefined);

  const suluRaw = record.suluStatus as string | undefined;
  const suluStatus = normalizeSuluStatus(suluRaw);

  const base = { ...record } as Customer & ApiCustomerRecord;
  delete (base as ApiCustomerRecord).onboardingStatuslabel;

  // The API sends the linked Jira ticket as a plain string (or null); the UI works with { key }.
  const jira = normalizeJiraFromApi(record.jira);

  const customerContact = normalizeCustomerContactFromApi(record);
  const masterAccount = normalizeMasterAccountFromApi(record);
  const biRoleName =
    readOptionalString(record, 'biRoleName', 'BiRoleName') ??
    readOptionalString(record, 'birolename');

  return {
    ...base,
    isAdmin: typeof record.isAdmin === 'boolean' ? record.isAdmin : false,
    onboardingStatus: normalizeOnboardingStatusFromApi(record.onboardingStatus as string),
    ...(onboardingStatusLabel ? { onboardingStatusLabel } : {}),
    ...(suluStatus ? { suluStatus } : {}),
    ...(jira !== undefined ? { jira } : {}),
    ...(biRoleName ? { biRoleName } : {}),
    ...(customerContact ? { customerContact } : {}),
    ...(masterAccount ? { masterAccount } : {}),
  };
};

const readOptionalString = (
  record: ApiCustomerRecord,
  ...keys: string[]
): string | undefined => {
  for (const key of keys) {
    const v = record[key];
    if (typeof v === 'string' && v.trim()) {
      return v.trim();
    }
  }
  return undefined;
};

export const normalizeCustomerListResponse = (payload: unknown): NormalizedCustomerList => {
  if (Array.isArray(payload)) {
    const customers = payload.map((item) =>
      normalizeCustomerApiRecord(item as ApiCustomerRecord)
    );
    return { customers, totalItemCount: customers.length };
  }

  if (payload && typeof payload === 'object' && Array.isArray((payload as CustomerListResponse).customers)) {
    const typed = payload as CustomerListResponse;
    const customers = typed.customers.map((item) => normalizeCustomerApiRecord(item));
    return {
      customers,
      totalItemCount: typed.totalItemCount ?? customers.length,
    };
  }

  return { customers: [], totalItemCount: 0 };
};
